import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal, MaskLines, Stagger, StaggerItem } from "@/components/site/Reveal";
import { SectionLabel, EnquiryBanner, PrimaryLink } from "@/components/site/Sections";
import {
  TRUST_STRIP, PRODUCTS, PRODUCT_SEGMENTS, MILLS, WHY, TIMELINE, INDUSTRIES, SUSTAINABILITY_POINTS, CONTACT,
} from "@/lib/data";

const FOREST_IMG = "https://images.unsplash.com/photo-1506452305024-9d3f02d1c9b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwaW5lJTIwZm9yZXN0JTIwbWlzdHxlbnwwfHx8fDE3ODQwMzk0NjZ8MA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  return (
    <section
      data-testid="hero"
      className="relative min-h-screen flex flex-col justify-between overflow-hidden"
      style={{
        background:
          "radial-gradient(120% 90% at 15% 0%, #EEF3F7 0%, rgba(238,243,247,0) 55%), radial-gradient(120% 120% at 90% 110%, #163024 0%, rgba(22,48,36,0) 60%), linear-gradient(165deg, #E7EFF3 0%, #CFE3D7 34%, #6FA98A 66%, #245038 100%)",
      }}
    >
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 lg:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-3 rounded-full bg-white/60 backdrop-blur-md border border-white/50 px-5 py-2"
        >
          <span className="h-2 w-2 rounded-full bg-forest" />
          <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-forest">
            Serving the paper trade since 1970
          </span>
        </motion.div>

        <h1 className="mt-8 font-serif tracking-tighter leading-[0.94] text-forest-deep text-5xl sm:text-7xl lg:text-[6.8rem] max-w-5xl">
          <MaskLines lines={["Your trusted paper", "& board partner", "for the modern trade."]} delay={0.15} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-8 max-w-xl text-lg sm:text-xl leading-relaxed text-ink"
        >
          Three generations of reliable paper and board sourcing for printers, publishers and
          packaging companies — built on deep experience, strong mill relationships and a
          commitment to sustainable paper.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <PrimaryLink to="/contact" testId="hero-enquiry-cta">Send Enquiry</PrimaryLink>
          <PrimaryLink to="/products" testId="hero-products-cta" secondary>Explore Products</PrimaryLink>
        </motion.div>
      </div>

      {/* bottom band with stats on the green */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.15 }}
        className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14 lg:pb-16"
      >
        <div className="flex flex-wrap items-end gap-x-12 gap-y-6">
          <div className="flex items-center gap-2 text-paper/90">
            <ArrowDown className="h-4 w-4 animate-bounce" />
            <span className="font-mono text-xs uppercase tracking-[0.24em]">Scroll to discover</span>
          </div>
          <div className="ml-auto flex items-end gap-10">
            {[["1970", "Since"], ["3", "Generations"], ["4+", "Trusted mills"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-4xl lg:text-5xl text-paper leading-none">{v}</p>
                <p className="mt-2 text-xs text-paper/70 tracking-wide">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const TrustStrip = () => (
  <section data-testid="trust-strip" className="relative z-10 border-y border-paper-border bg-paper-soft/60">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Stagger className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-paper-border">
        {TRUST_STRIP.map((t) => (
          <StaggerItem key={t.label} className="px-6 py-10 lg:py-14 first:pl-0">
            <p className="font-serif text-5xl lg:text-6xl text-forest tracking-tight">{t.value}</p>
            <p className="mt-3 text-sm text-ink-soft leading-snug">{t.label}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const AboutPreview = () => (
  <section data-testid="about-preview" className="relative z-10 py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <div className="lg:col-span-6">
        <Reveal><SectionLabel>Our story</SectionLabel></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-forest">
            A family name the paper trade has trusted for over fifty years.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-8 text-lg text-ink-soft leading-relaxed">
            Founded in 1970 by <span className="text-forest font-medium">Meghraj Jain</span>, carried
            forward by <span className="text-forest font-medium">Dinesh Jain</span>, and today led
            together with the third generation, <span className="text-forest font-medium">Milan Jain</span> —
            Mysore Paper pairs deep industry knowledge with responsive, relationship-driven service.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-10"><PrimaryLink to="/about" testId="about-preview-cta" secondary>Read our heritage</PrimaryLink></div>
        </Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal delay={0.1} className="relative">
          <div className="overflow-hidden rounded-sm">
            <img
              src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcmludGluZyUyMHByZXNzfGVufDB8fHx8MTc4NDAzOTQ2NXww&ixlib=rb-4.1.0&q=85"
              alt="Modern printing press producing commercial print"
              className="w-full h-[520px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-forest text-paper px-8 py-6 rounded-sm hidden sm:block">
            <p className="font-serif text-4xl">1970</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper/60 mt-1">Where it began</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ProductsPreview = () => (
  <section data-testid="products-preview" className="relative z-10 py-24 lg:py-32 bg-paper-soft/50 border-y border-paper-border">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
        <div>
          <Reveal><SectionLabel>Product portfolio</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-forest max-w-2xl">
              Quality paper &amp; board for print and packaging.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.16}><PrimaryLink to="/products" testId="products-preview-cta" secondary>View all products</PrimaryLink></Reveal>
      </div>

      <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {PRODUCT_SEGMENTS.map((seg) => {
          const items = PRODUCTS.filter((p) => p.segment === seg.id);
          return (
            <StaggerItem key={seg.id}>
              <Link to="/products" data-testid={`portfolio-card-${seg.id}`} className="group flex h-full flex-col bg-surface rounded-sm border border-paper-border overflow-hidden">
                <div className="overflow-hidden">
                  <img src={items[0].image} alt={seg.title} className="h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">{`0${PRODUCT_SEGMENTS.indexOf(seg) + 1} · ${items.length} grades`}</p>
                  <h3 className="mt-3 font-serif text-2xl text-forest leading-tight">{seg.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{seg.note}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {items.map((it) => (
                      <span key={it.slug} className="rounded-full border border-paper-border bg-paper-soft/60 px-3 py-1 text-[11px] text-forest">
                        {it.name}
                      </span>
                    ))}
                  </div>
                  <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-forest">
                    Explore range <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const MillsMarquee = () => (
  <section data-testid="mills-marquee" className="relative z-10 py-16 lg:py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-10">
      <Reveal><SectionLabel>Trusted mill associations</SectionLabel></Reveal>
    </div>
    <Marquee speed={40} gradient gradientColor="#FCFBF9" gradientWidth={120}>
      {[...MILLS, ...MILLS].map((m, i) => (
        <span key={i} className="mx-12 font-serif text-3xl lg:text-5xl text-forest/80 tracking-tight flex items-center gap-12">
          {m.name}
          <span className="text-gold text-2xl">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose" className="relative z-10 py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-2xl mb-16">
        <Reveal><SectionLabel>Why Mysore Paper</SectionLabel></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-forest">
            More than a supplier — a sourcing partner.
          </h2>
        </Reveal>
      </div>
      <Stagger className="grid gap-px bg-paper-border border border-paper-border rounded-sm overflow-hidden md:grid-cols-2 lg:grid-cols-3">
        {WHY.map((w, i) => (
          <StaggerItem key={w.title} className="bg-paper p-9 lg:p-10">
            <span className="font-mono text-sm text-gold">0{i + 1}</span>
            <h3 className="mt-5 font-serif text-2xl text-forest leading-tight">{w.title}</h3>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed">{w.body}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Heritage = () => (
  <section data-testid="heritage-timeline" className="relative z-10 py-24 lg:py-32 bg-paper-soft/50 border-y border-paper-border">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-2xl mb-16">
        <Reveal><SectionLabel>Heritage timeline</SectionLabel></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-forest">
            Built by three generations of one family.
          </h2>
        </Reveal>
      </div>
      <div className="grid gap-10 lg:grid-cols-3">
        {TIMELINE.map((t, i) => (
          <Reveal key={t.year} delay={i * 0.12} className="relative">
            <div className="border-t-2 border-forest pt-8">
              <p className="font-serif text-6xl lg:text-7xl text-forest tracking-tight">{t.year}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.24em] text-gold">{t.person}</p>
              <h3 className="mt-4 font-serif text-2xl text-forest">{t.title}</h3>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">{t.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sustainability = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} data-testid="sustainability-home" className="relative z-10 overflow-hidden bg-forest text-paper">
      <motion.div style={{ y }} className="absolute inset-0 opacity-25">
        <img src={FOREST_IMG} alt="Misty pine forest representing renewable paper sourcing" className="h-[125%] w-full object-cover" />
      </motion.div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel dark>Sustainability</SectionLabel>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
                Paper is the sustainable choice.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 text-lg text-paper/70 leading-relaxed">
                Renewable, recyclable and biodegradable — paper remains one of the most responsible
                materials in the world. We help buyers choose grades that are good for business and
                the planet.
              </p>
            </Reveal>
            <Reveal delay={0.24}><div className="mt-10"><PrimaryLink to="/sustainability" testId="home-sustainability-cta" secondary>Why paper matters</PrimaryLink></div></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Stagger className="grid sm:grid-cols-2 gap-px bg-paper/15 border border-paper/15 rounded-sm overflow-hidden">
              {SUSTAINABILITY_POINTS.map((s) => (
                <StaggerItem key={s.title} className="bg-forest p-8">
                  <h3 className="font-serif text-xl text-paper">{s.title}</h3>
                  <p className="mt-3 text-sm text-paper/60 leading-relaxed">{s.body}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
};

const IndustriesStrip = () => (
  <section data-testid="industries-home" className="relative z-10 py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <Reveal><SectionLabel>Industries served</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-forest">
              Trusted across the print &amp; packaging ecosystem.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.16}><PrimaryLink to="/industries" testId="home-industries-cta" secondary>See all industries</PrimaryLink></Reveal>
      </div>
      <Stagger className="flex flex-wrap gap-3">
        {INDUSTRIES.map((ind) => (
          <StaggerItem key={ind.name}>
            <span className="inline-block rounded-full border border-paper-border bg-surface px-6 py-3 text-base font-medium text-forest transition-colors duration-300 hover:bg-forest hover:text-paper hover:border-forest cursor-default">
              {ind.name}
            </span>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <TrustStrip />
    <AboutPreview />
    <ProductsPreview />
    <MillsMarquee />
    <WhyChoose />
    <Heritage />
    <Sustainability />
    <IndustriesStrip />
    <EnquiryBanner />
  </div>
);

export default Home;
